import React, { Fragment, useEffect, useState } from "react";
import CommonCard from "../../common/components/CommonCard";
import { Box, Divider, Grid, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import CommonButton from "../../common/components/CommonButton";
import { Add, BorderColor, Close, DeleteForever } from "@mui/icons-material";
import styled from "@emotion/styled";
import theme from "../../theme";
import moment from "moment";
import CommonSelect from "../../common/components/CommonSelect";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { setLoading } from "../../features/authSlice";
import { useDispatch, useSelector } from "react-redux";
import CommonDialog from "../../common/components/CommonDialog";
import CommonFormField from "../../common/components/CommonFormField";
import { ServiceOffer, TasksPriorityArr, TaskStatus, TaskStatusDispaly, UserTypeId } from "../../common/const";
import { CreateTask, DeleteTask, getTasks, UpdateTask } from "../../api/satactApi";

const Cell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    padding: 5,
    textAlign: 'center',
    fontSize: 13,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
    background: theme.palette.secondary.main,
    color: theme.palette.primary.main
}));

const InnerCell = styled(TableCell)(({ theme }) => ({
    padding: 10,
    textAlign: 'center',
    fontSize: 14,
    color: theme.palette.background.dark,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 1,
    height: 'auto', // Let it adjust based on content
    maxHeight: '400px', // Fixed maximum height
    overflow: 'auto', // Enable scrolling
    marginTop: 10
}));

const WorkLogs = ({
    fromMentor,
    projectData,
    mileStones,
    studentId,
    studentData,
    coachData
}) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const [open, setOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [taskId, setTaskId] = useState(null);
    const [errors, setErrors] = useState({});
    const [tasks, setTasks] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        assigner: '',
        start_date: '',
        end_date: '',
        priority: '',
        notes: '',
        context: ''
    });

    const validateField = (name, value) => {
        switch (name) {
            case 'name':
                if (!value) return 'Task Name is required';
                return '';
            case 'start_date':
                if (!value) return 'Start Date is required';
                return '';
            case 'end_date':
                if (!value) return 'Due Date is required';
                return '';
            case 'priority':
                if (!value) return 'Priority is required';
                return '';
            case 'assigner':
                if (!value) return 'Assigner is required';
                return '';
            case 'context':
                if (!value) return 'Milestone is required';
                return '';
            default:
                return '';
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key]);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        if (errors[name]) {
            const error = validateField(name, value);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const handleSubmit = async () => {
        try {
            if (!validateForm()) return;
            dispatch(setLoading({
                loading: true
            }));
            let response;
            if (taskId) {
                response = await UpdateTask(taskId, {
                    title: formData?.name,
                    description: formData?.notes,
                    priority: formData?.priority,
                    start_date: new Date(formData?.start_date)?.toISOString(),
                    due_date: new Date(formData?.end_date)?.toISOString(),
                    created_by: formData.assigner === 'SELF' ? user?.user_id : coachData?.mentor?.user_id,
                    assigned_by: formData.assigner === 'SELF' ? user?.user_id : coachData?.mentor?.user_id,
                    student_id: user?.student_id || studentId,
                    status: TaskStatus.PENDING,
                    type: 'PROJECT_MENTORING',
                    mentor_profile_id: projectData?.mentor_profile_id,
                    service_offers: ServiceOffer.mentor,
                    milestone_id: formData?.context
                })
            } else {
                const data = new FormData();
                data.append('title', formData.name);
                data.append('description', formData.notes);
                data.append('priority', formData.priority);
                data.append('start_date', new Date(formData.start_date).toISOString());
                data.append('due_date', new Date(formData.end_date).toISOString());
                data.append('created_by', formData.assigner === 'SELF' ? user?.user_id : coachData?.mentor?.user_id);
                data.append('assigned_by', formData.assigner === 'SELF' ? user?.user_id : coachData?.mentor?.user_id);
                data.append('student_id', user?.student_id || studentId);
                data.append('status', TaskStatus.PENDING);
                data.append('type', 'PROJECT_MENTORING');
                data.append('mentor_profile_id', projectData?.mentor_profile_id);
                data.append('service_offers', ServiceOffer.mentor);
                data.append('milestone_id', formData.context);
                data.append('project_id', projectData?.project_id);
                response = await CreateTask(data);
            }
            if (response.success) {
                showNotification(response.message, 'success');
                setFormData({
                    name: '',
                    assigner: '',
                    start_date: '',
                    end_date: '',
                    priority: '',
                    notes: '',
                    context: ''
                });
                setOpen(false);
                getTaskData();
                dispatch(setLoading({
                    loading: false
                }));
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    const getTaskData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getTasks('PROJECT_MENTORING', user?.student_id || studentId, projectData?.project_id);
            setTasks(response?.data?.filter((a) => a.created_by === studentData?.user_id));
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        if (projectData?.project_id && (user?.student_id || studentId)) {
            getTaskData();
        }
    }, [projectData, studentId, user, studentData]);


    return (
        <Fragment>
            <CommonDialog
                open={open}
                size="xs"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>{taskId ? 'Update Task' : 'Create New Task'}</Typography>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setOpen(false);
                                    setErrors({});
                                    setFormData({
                                        name: '',
                                        assigner: '',
                                        start_date: '',
                                        end_date: '',
                                        priority: '',
                                        notes: '',
                                        context: ''
                                    });
                                    setTaskId(null);
                                }}
                            />
                        </Box>
                        <Box width="80%" mb={0.5}>
                            <Typography sx={{ fontSize: 12 }} color={theme.palette.background.dark}>Enter the task details</Typography>
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container spacing={2} mt={0.5} mb={1}>
                        <Grid item md={12} xs={12}>
                            <CommonFormField
                                label="Task Name"
                                type="email"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                error={errors.name}
                                placeholder="Enter Task Name"
                            />
                        </Grid>
                        <Grid item md={12} xs={12}>
                            <CommonSelect
                                label='Assigner'
                                name="assigner"
                                value={formData.assigner}
                                onChange={handleInputChange}
                                placeholder='Select Assigner'
                                error={errors.assigner}
                                options={[{
                                    label: 'Self', value: 'SELF'
                                }, {
                                    label: 'Mentor', value: 'MENTOR'
                                }]}
                            />
                        </Grid>
                        <Grid item md={12} xs={12}>
                            <CommonSelect
                                label={'Linked Milestone'}
                                name="context"
                                value={formData.context}
                                onChange={handleInputChange}
                                error={errors.context}
                                placeholder={'Select Milestone'}
                                options={mileStones?.map((a) => ({
                                    label: a?.milestone_name,
                                    value: a?.milestone_id
                                }))}
                            />
                        </Grid>
                        <Grid item md={12} xs={12}>
                            <CommonSelect
                                label="Priority"
                                name="priority"
                                value={formData.priority}
                                onChange={handleInputChange}
                                placeholder="Select Priority"
                                error={errors.priority}
                                options={TasksPriorityArr}
                            />
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <CommonFormField
                                label="Start Date"
                                type="date"
                                name="start_date"
                                value={formData.start_date}
                                onChange={handleInputChange}
                                error={errors.start_date}
                                placeholder="Select Start Date"
                            />
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <CommonFormField
                                label="Due Date"
                                type="date"
                                name="end_date"
                                value={formData.end_date}
                                onChange={handleInputChange}
                                error={errors.end_date}
                                placeholder="Select Due Date"
                            />
                        </Grid>
                        <Grid item md={12} xs={12}>
                            <CommonFormField
                                label="Description"
                                type="email"
                                name="notes"
                                value={formData.notes}
                                onChange={handleInputChange}
                                placeholder="Describe what success"
                                multiline
                            />
                        </Grid>
                    </Grid>
                }
                actionButtons={(
                    <CommonButton
                        label={taskId ? "Update" : "Create"}
                        size="small"
                        background={theme.palette.secondary.main}
                        color={theme.palette.background.main}
                        fontWeight={700}
                        marginBottom="10px"
                        handleClick={() => {
                            handleSubmit();
                        }}
                    />
                )}
            />
            <CommonDialog
                open={deleteOpen}
                size="xs"
                content={(
                    <center>
                        <Typography sx={{ fontWeight: 600, fontSize: '22px', mt: 0.5, mb: 1 }}>
                            Are you sure you want to delete task?
                        </Typography>
                    </center>
                )}
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label="Yes"
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.primary.main}
                            handleClick={async () => {
                                try {
                                    dispatch(setLoading({
                                        loading: true
                                    }));
                                    const response = await DeleteTask(taskId);
                                    if (response.success) {
                                        showNotification(response.message, 'success');
                                        setDeleteOpen(false);
                                        dispatch(setLoading({
                                            loading: false
                                        }));
                                        getTaskData();
                                    }
                                } catch (err) {
                                    const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                    showNotification(errorMessage, 'error');
                                    dispatch(setLoading({
                                        loading: false
                                    }));
                                }
                            }}
                        />
                        <CommonButton
                            label="No"
                            size="small"
                            background={theme.palette.background.main}
                            border={`1px solid ${theme.palette.background.light}`}
                            handleClick={() => {
                                setTaskId(null);
                                setDeleteOpen(false);
                            }}
                        />
                    </Fragment>
                )}
            />
            <CommonCard
                paddingBottom="0px"
                borderRadius={2}
                content={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography fontWeight={600}>Task & Work Logs</Typography>
                            {!fromMentor && user?.user_type_id === UserTypeId.Student && (
                                <CommonButton
                                    label="Create Task"
                                    height="35px"
                                    startIcon={<Add />}
                                    width="150px"
                                    marginBottom="0px"
                                    handleClick={() => setOpen(true)}
                                />
                            )}
                        </Box>
                        <StyledTableContainer>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                                        <Cell width="17%">Task Name</Cell>
                                        <Cell width="11%">Date</Cell>
                                        <Cell width="11%">Assigner</Cell>
                                        <Cell width="12%">Priority</Cell>
                                        <Cell style={{ textAlign: 'left' }} width="20%">MileStone</Cell>
                                        <Cell width="20%">{fromMentor || user?.user_type_id !== UserTypeId.Student ? 'Task Status' : 'Action'}</Cell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tasks.length > 0 ? tasks.map((note, index) => (
                                        <TableRow key={index}>
                                            <InnerCell style={{ color: 'black' }}>
                                                {note.title}
                                            </InnerCell>
                                            <InnerCell>
                                                {moment(note.start_date).format('MMM DD, YYYY')}
                                            </InnerCell>
                                            <InnerCell>
                                                {`${note?.assigned?.first_name} ${note?.assigned?.last_name}`}
                                            </InnerCell>
                                            <InnerCell>
                                                <Box>
                                                    <Box
                                                        sx={{
                                                            background: note.priority === 'MEDIUM' ? theme.palette.secondary.main : note.priority === 'HIGH' ? 'red' : '#01875F',
                                                            padding: '3px 5px',
                                                            width: '80%',
                                                            marginBottom: 0.5,
                                                            borderRadius: 1,
                                                        }}
                                                    >
                                                        <Typography fontSize={13} color={note.priority === 'MEDIUM' ? theme.palette.primary.main : note.priority === 'HIGH' ? 'white' : 'white'}>{TasksPriorityArr.find((a) => a.value === note.priority)?.label}</Typography>
                                                    </Box>
                                                </Box>
                                            </InnerCell>
                                            <InnerCell style={{ textAlign: 'left' }}>
                                                {note.milestone?.milestone_name}
                                            </InnerCell>
                                            <InnerCell>
                                                {!fromMentor && user?.user_type_id === UserTypeId.Student && (
                                                    <Fragment>
                                                        <IconButton size="small" onClick={() => {
                                                            setTaskId(note?.task_id);
                                                            setFormData({
                                                                name: note?.title,
                                                                assigner: 'SELF',
                                                                start_date: note?.start_date ? moment(note?.start_date)?.format('YYYY-MM-DD') : '',
                                                                end_date: note?.start_date ? moment(note?.due_date)?.format('YYYY-MM-DD') : '',
                                                                priority: note?.priority,
                                                                notes: note?.description,
                                                                context: note?.milestone_id
                                                            });
                                                            setOpen(true)
                                                        }}>
                                                            <BorderColor sx={{ color: theme.palette.secondary.main, fontSize: 17 }} />
                                                        </IconButton>
                                                        <IconButton size="small" onClick={() => {
                                                            setDeleteOpen(true);
                                                            setTaskId(note?.task_id)
                                                        }}>
                                                            <DeleteForever sx={{ color: 'red', fontSize: 20 }} />
                                                        </IconButton>
                                                    </Fragment>
                                                )}
                                                {!fromMentor && (note?.status === TaskStatus.PENDING || note?.status === TaskStatus.IN_PROGRESS) ? (
                                                    <CommonButton
                                                        label={note?.status === TaskStatus.PENDING ? "Start" : "Complete"}
                                                        fontWeight={600}
                                                        width={note?.status === TaskStatus.PENDING ? "50px" : "90px"}
                                                        height="30px"
                                                        marginBottom="0px"
                                                        handleClick={async () => {
                                                            try {
                                                                dispatch(setLoading({
                                                                    loading: true
                                                                }));
                                                                const response = await UpdateTask(note.task_id, {
                                                                    "status": note?.status === TaskStatus?.PENDING ? TaskStatus.IN_PROGRESS : TaskStatus.COMPLETED,
                                                                    "completed_at": new Date().toISOString()
                                                                });
                                                                if (response?.success) {
                                                                    showNotification(response.message, 'success');
                                                                    getTaskData();
                                                                }
                                                            } catch (err) {
                                                                const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                                                showNotification(errorMessage, 'error');
                                                                dispatch(setLoading({
                                                                    loading: false
                                                                }));
                                                            }
                                                        }}
                                                    />
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            background: '#01875F',
                                                            padding: '3px 10px',
                                                            width: '60%',
                                                            borderRadius: 1,
                                                            margin: '0 auto'
                                                        }}
                                                    >
                                                        <Typography fontSize={13} color={'white'} textAlign="center">{TaskStatusDispaly[note?.status]}</Typography>
                                                    </Box>
                                                )}
                                            </InnerCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">
                                                <Typography>No Task Found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </StyledTableContainer>
                    </Fragment>
                }
            />
        </Fragment>
    )
}

export default WorkLogs;